import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Sparkles,
  Search,
  AlertTriangle,
  BarChart3,
  Users,
  Kanban,
  Check,
} from "lucide-react";

/**
 * ActualFeatures — a scroll-linked, parallax feature tour.
 * Each row uses framer-motion useScroll to drive:
 *   • image translateY + subtle rotate + scale
 *   • text glides opposite direction
 *   • gradient blob drifts slower (deep parallax)
 *   • number badge slides + fades in
 *   • bullet checks stagger reveal
 * A left vertical rail tracks progress across all six stages.
 */

const FEATURES = [
  {
    id: "brand",
    icon: Sparkles,
    accent: "#0F5535",
    accentSoft: "#DFF0E6",
    blob: "radial-gradient(ellipse at 30% 40%, #0F5535 0%, transparent 55%), radial-gradient(ellipse at 70% 60%, #FFB000 0%, transparent 50%)",
    kicker: "Brand foundation · 60 seconds",
    title: "Paste one URL. Get your entire buyer map.",
    body:
      "Leadmato crawls your website, extracts positioning, spots your ICPs, generates 20+ buyer-intent keywords, and lists your closest competitors — before your coffee cools.",
    points: [
      "AI-crafted ICP profiles with pain points",
      "20+ buyer-intent keywords ready to scan",
      "Competitor list + positioning gaps",
    ],
    image: "/features/brand.png",
    alt: "Brand foundation onboarding with AI-generated ICPs and keywords",
  },
  {
    id: "find-customers",
    icon: Search,
    accent: "#D95A40",
    accentSoft: "#FCE4DB",
    blob: "radial-gradient(ellipse at 20% 30%, #D95A40 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, #2E4EFF 0%, transparent 50%)",
    kicker: "Find your next customer",
    title: "Every buyer conversation, ranked before you finish your chai.",
    body:
      "One query, every public channel. Reddit threads, forum boards, LinkedIn discussions and X posts — each signal scored 0–100 by buying intent, with sellers and off-topic noise filtered out first, so you never scroll past a real one.",
    points: [
      "Scan Reddit · forums · LinkedIn · X — tick any platform per run",
      "Opportunity score with transparent reasoning",
      "Matched keyword + intent on every lead",
    ],
    image: "/features/find-customers.png",
    alt: "Find Customers page showing scored signal cards from Reddit",
  },
  {
    id: "pain-points",
    icon: AlertTriangle,
    accent: "#DC2626",
    accentSoft: "#FEE2E2",
    blob: "radial-gradient(ellipse at 25% 35%, #DC2626 0%, transparent 55%), radial-gradient(ellipse at 75% 65%, #F59E0B 0%, transparent 50%)",
    kicker: "Turn frustration into positioning",
    title: "Cluster the complaints. Own the answer.",
    body:
      "Every scan feeds a live pain map — the phrases, frustrations and unmet needs your buyers repeat across platforms. Spot copy angles, product gaps and content ideas nobody else can see.",
    points: [
      "Auto-clustered themes with mention counts",
      "Sample signals + direct source links",
      "Sentiment + platform breakdown per theme",
    ],
    image: "/features/pain-points.png",
    alt: "Pain Points page clustering buyer complaints into themes",
  },
  {
    id: "market",
    icon: BarChart3,
    accent: "#7A3EA5",
    accentSoft: "#EEE1F7",
    blob: "radial-gradient(ellipse at 20% 30%, #7A3EA5 0%, transparent 55%), radial-gradient(ellipse at 75% 70%, #0EA5E9 0%, transparent 50%)",
    kicker: "Read the market like a chart",
    title: "Where your buyers hang out. Where they don't.",
    body:
      "Platform mix, intent distribution, sentiment shifts and 30-day signal velocity — the same data your analytics agency charges ₹80k a month to compile.",
    points: [
      "30-day signal velocity + score buckets",
      "Platform / intent / sentiment mix charts",
      "Top-performing keywords for your niche",
    ],
    image: "/features/market.png",
    alt: "Market Analysis with velocity chart and platform mix bars",
  },
  {
    id: "partners",
    icon: Users,
    accent: "#0EA5E9",
    accentSoft: "#E0F2FE",
    blob: "radial-gradient(ellipse at 30% 30%, #0EA5E9 0%, transparent 55%), radial-gradient(ellipse at 70% 70%, #14B8A6 0%, transparent 50%)",
    kicker: "Find the people who move your niche",
    title: "Every micro-influencer, ranked by signal.",
    body:
      "Leadmato ranks the authors and creators surfacing across your scans — including TikTok — the perfect list for outreach, collabs, guest content and affiliate partnerships. Sorted by frequency, average score and dominant intent.",
    points: [
      "Top authors ranked by signal + score",
      "Platform tags + dominant intent per author",
      "Direct links to profile and latest signal",
    ],
    image: "/features/partners.png",
    alt: "Partners leaderboard with ranked authors and signal counts",
  },
  {
    id: "shortlist",
    icon: Kanban,
    accent: "#B8813A",
    accentSoft: "#FEF3C7",
    blob: "radial-gradient(ellipse at 30% 40%, #B8813A 0%, transparent 55%), radial-gradient(ellipse at 70% 60%, #EF4444 0%, transparent 50%)",
    kicker: "Your outreach cockpit",
    title: "Save. Track. Close. Repeat.",
    body:
      "Move leads through New → Contacted → Replied → Closed on a kanban that remembers every note, tag and activity event. Your outreach doesn't sit in a Google Sheet anymore.",
    points: [
      "Drag-and-drop kanban with 4 stages",
      "Per-lead activity history + tag filtering",
      "Notes + source link saved on every lead",
    ],
    image: "/features/shortlist.png",
    alt: "Saved Leads kanban board with New / Contacted / Replied / Closed columns",
  },
];

export default function ActualFeatures() {
  return (
    <section
      id="product-tour"
      className="relative lm-cv bg-lm-bg overflow-hidden"
      data-testid="product-tour-section"
    >
      {/* Section intro */}
      <div className="max-w-6xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-6 md:pb-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full"
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
            What&apos;s inside Leadmato
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-semibold leading-[1.05] tracking-tight text-lm-text">
            Six features.<br />One tab. Zero SDR salaries.
          </h2>
          <p className="mt-5 text-base md:text-lg text-lm-text2 leading-relaxed">
            Every screen below is a real page inside the app you&apos;re about to open.
            Scroll through the tour — each frame animates in as you meet it.
          </p>
        </motion.div>
      </div>

      {/* Feature stages */}
      <div className="max-w-6xl mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <div className="relative">
          {FEATURES.map((f, i) => (
            <FeatureStage key={f.id} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureStage({ feature, index }) {
  const rowRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: rowRef,
    offset: ["start end", "end start"],
  });

  const reversed = index % 2 === 1;
  const Icon = feature.icon;

  // Image parallax — gentle vertical drift, no tilt
  const imgY = useTransform(scrollYProgress, [0, 0.5, 1], [50, 0, -50]);
  const imgOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0.75]);

  // Text glides
  const textY = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -30]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.65]);

  // Blob (deep parallax — slower)
  const blobY = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const blobScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1.05, 0.92]);

  // Number badge
  const badgeX = useTransform(scrollYProgress, [0, 0.4], [reversed ? 40 : -40, 0]);
  const badgeOpacity = useTransform(scrollYProgress, [0, 0.35], [0, 1]);

  const numberLabel = String(index + 1).padStart(2, "0");

  return (
    <div
      ref={rowRef}
      data-testid={`feature-stage-${feature.id}`}
      className="relative py-14 md:py-20 flex items-center"
    >
      {/* Parallax gradient blob */}
      <motion.div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{ y: blobY, scale: blobScale }}
      >
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[70%] opacity-[0.10] blur-3xl"
          style={{ backgroundImage: feature.blob }}
        />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center w-full">
        {/* Text side */}
        <motion.div
          style={{ y: textY, opacity: textOpacity }}
          className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}
        >
          <motion.div
            style={{ x: badgeX, opacity: badgeOpacity }}
            className="flex items-center gap-4 mb-6"
          >
            <div
              className="text-5xl md:text-6xl font-display font-semibold leading-none tracking-tight"
              style={{ color: feature.accent }}
            >
              {numberLabel}
            </div>
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: feature.accentSoft, color: feature.accent }}
            >
              <Icon className="w-5 h-5" strokeWidth={2.2} />
            </div>
          </motion.div>

          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-lm-text3 mb-3">
            {feature.kicker}
          </div>
          <h3 className="text-3xl md:text-[2.4rem] font-display font-semibold text-lm-text leading-[1.1] mb-5">
            {feature.title}
          </h3>
          <p className="text-base md:text-lg text-lm-text2 leading-relaxed mb-7 max-w-xl">
            {feature.body}
          </p>
          <ul className="space-y-3">
            {feature.points.map((p, pi) => (
              <motion.li
                key={p}
                initial={{ opacity: 0, x: reversed ? 12 : -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.45, delay: 0.15 + pi * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-3 text-[15px] text-lm-text"
              >
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-[1px]"
                  style={{ backgroundColor: feature.accent, color: "#fff" }}
                >
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
                <span className="leading-snug pt-[3px]">{p}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Image side */}
        <motion.div className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`}>
          <motion.div
            style={{ y: imgY, opacity: imgOpacity }}
            className="relative"
          >
            {/* accent glow behind card */}
            <div
              className="absolute -inset-6 rounded-[32px] opacity-40 blur-2xl"
              style={{ backgroundColor: feature.accent }}
              aria-hidden
            />
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#1a1d22]"
              style={{ boxShadow: `0 40px 100px -40px ${feature.accent}55, 0 12px 28px -18px rgba(0,0,0,0.35)` }}
            >
              {/* Mac-style dark browser chrome */}
              <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-white/10 bg-gradient-to-b from-[#2a2d33] to-[#1f2226]">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57] shadow-inner" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e] shadow-inner" />
                <span className="w-3 h-3 rounded-full bg-[#28c840] shadow-inner" />
                <div className="flex-1 flex justify-center">
                  <span className="text-[10px] font-mono text-white/50 truncate px-3 py-0.5 rounded-md bg-black/25 border border-white/5">
                    /app/{feature.id}
                  </span>
                </div>
                <span className="w-[42px]" aria-hidden />
              </div>
              <div className="w-full bg-[#FAF7EE] overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.alt}
                  className="block w-full h-auto select-none"
                  draggable="false"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
